import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForecastResults from './ForecastResults';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  createChart,
  createSeriesMarkers,
} from 'lightweight-charts';

import {
  modelLabAssetApi,
  modelLabDeploymentApi,
  modelLabPredictionTestApi,
  modelLabVersionApi,
} from '../../services/api';
import type {
  ModelLabAsset,
  ModelLabDeployment,
  ModelLabPredictionTestRun,
  ModelLabVersion,
} from '../../types';
import useModelLabEventsSocket from '../../hooks/useModelLabEventsSocket';
import { getApiErrorMessage } from './utils';

interface ModelLabPredictionTestingPanelProps {
  modelAssetId?: number | null;
  compact?: boolean;
}

interface ChartRow {
  index: number;
  label: string;
  timestamp: string | null;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number;
  volume: number | null;
}

interface ConfidenceRow {
  index: number;
  label: string;
  confidence: number | null;
  margin: number | null;
  entropy: number | null;
  predictionLabel: string;
  isLowConfidence: boolean;
}

interface PredictionRow {
  row_index?: number;
  timestamp?: string | null;
  time?: number | string | null;
  open?: number | null;
  high?: number | null;
  low?: number | null;
  close?: number | null;
  prediction: any;
  prediction_label?: string;
  probability?: any;
  confidence?: number | null;
  actual?: any;
  actual_label?: string;
  residual?: number | null;
}

interface EventOverlay {
  id: number;
  source: string;
  event_type: string;
  title: string;
  timestamp: string | null;
  bar_index?: number | null;
  impact?: number | null;
  confidence?: number | null;
  tags?: string[];
  sentiment_direction?: string | null;
}

interface AttributionFeature {
  feature: string;
  value?: number | string | null;
  contribution: number;
  abs_contribution: number;
  direction: string;
  importance?: number;
}

interface AttributionRow {
  index: number;
  timestamp?: string | null;
  time?: number | string | null;
  prediction_label?: string;
  top_features: AttributionFeature[];
}

interface SymbolPreview {
  symbol: string;
  status: string;
  result: any | null;
  predictionRows: PredictionRow[];
  error: string | null;
  chartRows: ChartRow[];
  summary: Record<string, any>;
  confidencePanel: {
    available: boolean;
    threshold?: number;
    rows: ConfidenceRow[];
    summary: Record<string, any>;
  };
  eventOverlays: {
    available: boolean;
    events: EventOverlay[];
    edges: any[];
    summary: Record<string, any>;
  };
  attributionTimeline: {
    available: boolean;
    method?: string | null;
    rows: AttributionRow[];
    top_features: Array<{ feature: string; total_abs_contribution: number }>;
    note?: string;
  };
}

interface PreviewResult {
  testRunId: number;
  deploymentId: number;
  versionId: number | null;
  status: string;
  metrics: Record<string, any>;
  problemType: string;
  outputType: string;
  visualizationHints: Record<string, any>;
  symbols: SymbolPreview[];
}

const TIMEFRAMES = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1'];

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const parseSymbols = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean)
    .filter((item, index, list) => list.indexOf(item) === index);

const formatTimestamp = (value: unknown, fallbackIndex: number): string => {
  if (value === null || value === undefined || value === '') {
    return String(fallbackIndex + 1);
  }
  if (typeof value === 'number') {
    const milliseconds = value > 1_000_000_000_000 ? value : value * 1000;
    const date = new Date(milliseconds);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  }
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const normalizeChartRows = (rows: any[]): ChartRow[] =>
  (Array.isArray(rows) ? rows : [])
    .map((row: any, index: number) => {
      const close = toNumber(row.close ?? row.c);
      if (close === null) return null;
      const timestamp = row.timestamp ?? row.time ?? row.date ?? row.t ?? null;
      return {
        index: index + 1,
        label: formatTimestamp(timestamp, index),
        timestamp: timestamp === null ? null : String(timestamp),
        open: toNumber(row.open ?? row.o),
        high: toNumber(row.high ?? row.h),
        low: toNumber(row.low ?? row.l),
        close,
        volume: toNumber(row.volume ?? row.v),
      };
    })
    .filter((row): row is ChartRow => Boolean(row));

const extractPredictions = (result: any): any[] => {
  if (!result) return [];
  if (Array.isArray(result.predictions)) return result.predictions;
  if (Array.isArray(result.prediction)) return result.prediction;
  if (result.prediction !== undefined) return [result.prediction];
  if (result.value !== undefined) return [result.value];
  return [];
};

const predictionLabel = (prediction: any): string => {
  if (prediction === null || prediction === undefined) return '-';
  if (typeof prediction === 'object') {
    const value = prediction.label ?? prediction.class ?? prediction.value ?? prediction.prediction ?? prediction.action;
    return value === undefined ? JSON.stringify(prediction) : String(value);
  }
  if (typeof prediction === 'number') return Number.isInteger(prediction) ? String(prediction) : prediction.toFixed(5);
  return String(prediction);
};

const normalizeConfidenceRows = (panel: any): ConfidenceRow[] =>
  (Array.isArray(panel?.rows) ? panel.rows : []).map((row: any, index: number) => ({
    index: Number(row.index || index + 1),
    label: formatTimestamp(row.timestamp ?? row.time, index),
    confidence: toNumber(row.confidence),
    margin: toNumber(row.margin),
    entropy: toNumber(row.entropy),
    predictionLabel: String(row.prediction_label ?? '-'),
    isLowConfidence: Boolean(row.is_low_confidence),
  }));

const predictionTestToPreview = (testRun: ModelLabPredictionTestRun): PreviewResult => {
  const result = testRun.result || {};
  const symbols = Array.isArray(result.symbols) ? result.symbols : [];
  return {
    testRunId: testRun.id,
    deploymentId: testRun.deployment,
    versionId: testRun.model_version,
    status: testRun.status,
    metrics: testRun.metrics || {},
    problemType: String(result.problem_type || testRun.metrics?.problem_type || ''),
    outputType: String(result.output_type || testRun.metrics?.output_type || ''),
    visualizationHints: result.visualization_hints || {},
    symbols: symbols.map((item: any): SymbolPreview => {
      const predictionRows = Array.isArray(item.prediction_rows) ? item.prediction_rows : [];
      return {
        symbol: String(item.symbol || '').toUpperCase(),
        status: item.status || 'UNKNOWN',
        error: item.error || null,
        predictionRows,
        chartRows: normalizeChartRows(item.chart_rows || []),
        summary: item.summary || {},
        confidencePanel: {
          available: Boolean(item.confidence_panel?.available),
          threshold: toNumber(item.confidence_panel?.threshold) ?? undefined,
          rows: normalizeConfidenceRows(item.confidence_panel),
          summary: item.confidence_panel?.summary || {},
        },
        eventOverlays: {
          available: Boolean(item.event_overlays?.available),
          events: Array.isArray(item.event_overlays?.events) ? item.event_overlays.events : [],
          edges: Array.isArray(item.event_overlays?.edges) ? item.event_overlays.edges : [],
          summary: item.event_overlays?.summary || {},
        },
        attributionTimeline: {
          available: Boolean(item.attribution_timeline?.available),
          method: item.attribution_timeline?.method || null,
          rows: Array.isArray(item.attribution_timeline?.rows) ? item.attribution_timeline.rows : [],
          top_features: Array.isArray(item.attribution_timeline?.top_features) ? item.attribution_timeline.top_features : [],
          note: item.attribution_timeline?.note,
        },
        result: {
          predictions: predictionRows.map((row: any) => row.prediction),
          probabilities: predictionRows.map((row: any) => row.probability ?? row.confidence),
          rows: predictionRows,
        },
      };
    }),
  };
};

const metricChip = (label: string, value: React.ReactNode) => (
  <Chip
    key={label}
    size="small"
    variant="outlined"
    label={
      <Box component="span">
        <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
          {label}
        </Box>
        {value}
      </Box>
    }
  />
);

const compactPercent = (value: unknown): string => {
  const numeric = toNumber(value);
  return numeric === null ? '-' : `${(numeric * 100).toFixed(1)}%`;
};

const compactNumber = (value: unknown, digits = 4): string => {
  const numeric = toNumber(value);
  return numeric === null ? '-' : numeric.toFixed(digits);
};

const LABEL_COLORS = [
  '#f59e0b',
  '#22c55e',
  '#ef4444',
  '#38bdf8',
  '#a78bfa',
  '#14b8a6',
  '#eab308',
  '#f97316',
  '#ec4899',
];

const labelColor = (label: string): string => {
  let hash = 0;
  for (let index = 0; index < label.length; index += 1) {
    hash = ((hash << 5) - hash) + label.charCodeAt(index);
    hash |= 0;
  }
  return LABEL_COLORS[Math.abs(hash) % LABEL_COLORS.length];
};

const labelIntent = (label: string): 'bullish' | 'bearish' | 'neutral' => {
  const text = label.toLowerCase();
  if (/(buy|long|bull|up|rise|positive|trend up|class[_ -]?1)/.test(text)) return 'bullish';
  if (/(sell|short|bear|down|fall|negative|trend down|class[_ -]?2)/.test(text)) return 'bearish';
  return 'neutral';
};

const predictionMode = (problemType?: string, outputType?: string): 'classification' | 'regression' | 'action' | 'structured' => {
  const problem = String(problemType || '').toUpperCase();
  const output = String(outputType || '').toLowerCase();
  if (problem === 'REINFORCEMENT' || /(action|policy|rl|allocation)/.test(output)) return 'action';
  if (
    problem === 'CLASSIFICATION'
    || /(categorical|category|class|label|probability|probabilities|logit)/.test(output)
  ) return 'classification';
  if (
    problem === 'REGRESSION'
    || problem === 'TIME_SERIES'
    || /(numeric|number|regression|forecast|price|continuous|timeseries|time_series)/.test(output)
  ) return 'regression';
  return 'structured';
};

const modeLabel = (mode: ReturnType<typeof predictionMode>): string => {
  if (mode === 'classification') return 'classification';
  if (mode === 'regression') return 'regression / forecast';
  if (mode === 'action') return 'action / policy';
  return 'structured output';
};

interface LegendItem {
  key: string;
  label: string;
  description: string;
  color?: string;
  secondaryColor?: string;
  shape?: 'line' | 'dot' | 'square' | 'bar' | 'candle';
}

const LegendSwatch: React.FC<{ item: LegendItem }> = ({ item }) => {
  if (item.shape === 'candle') {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, width: 22, height: 14 }}>
        <Box sx={{ width: 8, height: 14, bgcolor: item.color || '#22c55e', borderRadius: 0.3 }} />
        <Box sx={{ width: 8, height: 14, bgcolor: item.secondaryColor || '#ef4444', borderRadius: 0.3 }} />
      </Box>
    );
  }
  if (item.shape === 'line') {
    return <Box sx={{ width: 22, height: 0, borderTop: `3px solid ${item.color || '#fff'}`, borderRadius: 1 }} />;
  }
  if (item.shape === 'bar') {
    return <Box sx={{ width: 20, height: 12, bgcolor: item.color || '#fff', borderRadius: 0.4 }} />;
  }
  if (item.shape === 'square') {
    return <Box sx={{ width: 11, height: 11, bgcolor: item.color || '#fff', borderRadius: 0.3 }} />;
  }
  return <Box sx={{ width: 11, height: 11, bgcolor: item.color || '#fff', borderRadius: '50%' }} />;
};

const parseChartTime = (value: unknown, fallbackIndex: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.floor(value > 1_000_000_000_000 ? value / 1000 : value);
  }
  if (value !== null && value !== undefined && value !== '') {
    const parsed = new Date(String(value)).getTime();
    if (Number.isFinite(parsed)) return Math.floor(parsed / 1000);
  }
  return 1_700_000_000 + (fallbackIndex * 60);
};

const numericPredictionValue = (prediction: any): number | null => {
  if (typeof prediction === 'number') return Number.isFinite(prediction) ? prediction : null;
  if (prediction && typeof prediction === 'object') {
    for (const key of ['value', 'prediction', 'forecast', 'price', 'yhat']) {
      const numeric = toNumber(prediction[key]);
      if (numeric !== null) return numeric;
    }
  }
  return toNumber(prediction);
};

const isPriceLikePrediction = (prices: number[], predictions: number[]): boolean => {
  if (!prices.length || !predictions.length) return false;
  const sortedPrices = [...prices].sort((a, b) => a - b);
  const sortedPredictions = [...predictions].sort((a, b) => a - b);
  const medianPrice = sortedPrices[Math.floor(sortedPrices.length / 2)];
  const medianPrediction = sortedPredictions[Math.floor(sortedPredictions.length / 2)];
  if (!Number.isFinite(medianPrice) || !Number.isFinite(medianPrediction) || medianPrice === 0) {
    return false;
  }
  const ratio = Math.abs(medianPrediction / medianPrice);
  return ratio > 0.25 && ratio < 4;
};

const formatHoverTime = (timestamp?: string | null): string => (
  timestamp ? formatTimestamp(timestamp, 0) : '-'
);

interface PredictionReplayChartProps {
  preview: SymbolPreview;
  problemType: string;
  outputType: string;
  visualizationHints?: Record<string, any>;
  compact?: boolean;
}

const PredictionReplayChart: React.FC<PredictionReplayChartProps> = ({
  preview,
  problemType,
  outputType,
  visualizationHints = {},
  compact = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<any>(null);
  const [showPredictions, setShowPredictions] = useState(true);
  const [showEvents, setShowEvents] = useState(true);
  const [showConfidence, setShowConfidence] = useState(true);
  const [showVolume, setShowVolume] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hover, setHover] = useState<Record<string, any> | null>(null);

  const mode = predictionMode(problemType, outputType || preview.summary.output_type);
  const chartData = useMemo(() => {
    let lastTime = 0;
    const points = preview.chartRows.map((row, index) => {
      const rawTime = parseChartTime(row.timestamp ?? row.label, index);
      const time = rawTime <= lastTime ? lastTime + 1 : rawTime;
      lastTime = time;
      const open = row.open ?? row.close;
      const high = row.high ?? Math.max(open, row.close);
      const low = row.low ?? Math.min(open, row.close);
      return {
        ...row,
        rowIndex: index + 1,
        time,
        open,
        high,
        low,
        close: row.close,
      };
    });

    const pointByTimestamp = new Map<string, typeof points[number]>();
    points.forEach((point) => {
      if (point.timestamp) pointByTimestamp.set(point.timestamp, point);
    });

    const predictionOffset = Math.max(0, points.length - preview.predictionRows.length);
    const predictions = preview.predictionRows
      .map((row, index) => {
        const parsedTime = row.timestamp || row.time ? parseChartTime(row.timestamp ?? row.time, predictionOffset + index) : null;
        const exactPoint = row.timestamp ? pointByTimestamp.get(String(row.timestamp)) : null;
        const fallbackPoint = points[predictionOffset + index] || points[index] || points[points.length - 1];
        const point = exactPoint || (parsedTime ? points.find((item) => item.time === parsedTime) : null) || fallbackPoint;
        if (!point) return null;
        const label = String(row.prediction_label ?? predictionLabel(row.prediction));
        const numeric = numericPredictionValue(row.prediction);
        const actual = numericPredictionValue(row.actual);
        return {
          ...row,
          time: point.time,
          close: point.close,
          high: point.high,
          low: point.low,
          label,
          numeric,
          actual,
          confidence: toNumber(row.confidence),
          residual: toNumber(row.residual),
        };
      })
      .filter((row): row is NonNullable<typeof row> => Boolean(row));

    const numericValues = predictions
      .map((row) => row.numeric)
      .filter((value): value is number => value !== null && Number.isFinite(value));
    const priceLike = mode === 'regression' && isPriceLikePrediction(points.map((row) => row.close), numericValues);
    const predictionLineData = predictions
      .filter((row) => row.numeric !== null)
      .map((row) => ({ time: row.time, value: Number(row.numeric) }));
    const actualLineData = predictions
      .filter((row) => row.actual !== null)
      .map((row) => ({ time: row.time, value: Number(row.actual) }));
    const residualData = predictions
      .filter((row) => row.residual !== null)
      .map((row) => ({
        time: row.time,
        value: Number(row.residual),
        color: Number(row.residual) >= 0 ? 'rgba(34,197,94,0.75)' : 'rgba(239,68,68,0.75)',
      }));

    const classIndex = new Map<string, number>();
    const classTimeline = predictions.map((row) => {
      if (!classIndex.has(row.label)) classIndex.set(row.label, classIndex.size + 1);
      return {
        time: row.time,
        value: 1,
        color: labelColor(row.label),
      };
    });

    const eventPoints = (preview.eventOverlays.events || [])
      .map((event, index) => {
        const barIndex = toNumber(event.bar_index);
        let point = barIndex !== null ? points[Math.max(0, Math.min(points.length - 1, barIndex - 1))] : null;
        if (!point && event.timestamp) {
          const eventTime = parseChartTime(event.timestamp, index);
          point = points.reduce<typeof points[number] | null>((best, candidate) => {
            if (!best) return candidate;
            return Math.abs(candidate.time - eventTime) < Math.abs(best.time - eventTime) ? candidate : best;
          }, null);
        }
        if (!point) return null;
        return {
          ...event,
          time: point.time,
          close: point.close,
          high: point.high,
          low: point.low,
        };
      })
      .filter((row): row is NonNullable<typeof row> => Boolean(row));

    const confidenceData = preview.confidencePanel.rows
      .map((row, index) => {
        const point = points[Math.max(0, points.length - preview.confidencePanel.rows.length) + index];
        const value = toNumber(row.confidence);
        if (!point || value === null) return null;
        return { time: point.time, value };
      })
      .filter((row): row is { time: number; value: number } => Boolean(row));

    const volumeData = points
      .map((row) => {
        const value = toNumber((row as any).volume);
        if (value === null) return null;
        return {
          time: row.time,
          value,
          color: row.close >= row.open ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)',
        };
      })
      .filter((row): row is { time: number; value: number; color: string } => Boolean(row));

    const hoverByTime = new Map<number, Record<string, any>>();
    points.forEach((point) => {
      hoverByTime.set(point.time, { price: point });
    });
    predictions.forEach((row) => {
      hoverByTime.set(row.time, { ...(hoverByTime.get(row.time) || {}), prediction: row });
    });
    eventPoints.forEach((event) => {
      const existing = hoverByTime.get(event.time) || {};
      hoverByTime.set(event.time, {
        ...existing,
        events: [...(existing.events || []), event],
      });
    });

    return {
      points,
      candles: points.map((row) => ({
        time: row.time,
        open: row.open,
        high: row.high,
        low: row.low,
        close: row.close,
      })),
      predictions,
      predictionLineData,
      actualLineData,
      residualData,
      classTimeline,
      eventPoints,
      confidenceData,
      volumeData,
      hoverByTime,
      priceLike,
    };
  }, [mode, preview]);

  const classLabels = useMemo(() => {
    const labels: string[] = [];
    const add = (value: unknown) => {
      if (value === null || value === undefined || value === '') return;
      const label = String(value);
      if (!labels.includes(label)) labels.push(label);
    };
    const summaryLabels = preview.summary.class_labels;
    if (Array.isArray(summaryLabels)) summaryLabels.forEach(add);
    const distributionAll = preview.summary.distribution_all;
    if (distributionAll && typeof distributionAll === 'object') {
      Object.keys(distributionAll).forEach(add);
    }
    chartData.predictions.forEach((row) => add(row.label));
    return labels.slice(0, 16);
  }, [chartData.predictions, preview.summary]);

  const predictedLabelSet = useMemo(
    () => new Set(chartData.predictions.map((row) => row.label)),
    [chartData.predictions],
  );

  const missingPredictedClasses = useMemo(
    () => (Array.isArray(preview.summary.missing_predicted_classes)
      ? preview.summary.missing_predicted_classes.map((item: unknown) => String(item))
      : classLabels.filter((label) => !predictedLabelSet.has(label))),
    [classLabels, predictedLabelSet, preview.summary.missing_predicted_classes],
  );

  const legendItems = useMemo<LegendItem[]>(() => {
    const items: LegendItem[] = [
      {
        key: 'candles',
        label: 'OHLC candles',
        description: 'Green closes above open; red closes below open.',
        color: '#22c55e',
        secondaryColor: '#ef4444',
        shape: 'candle',
      },
    ];
    if (showPredictions) {
      if (mode === 'regression') {
        items.push({
          key: 'prediction-line',
          label: chartData.priceLike ? 'Predicted price' : 'Predicted value',
          description: chartData.priceLike
            ? 'Regression forecast drawn on the price scale.'
            : 'Regression forecast drawn in its own value pane.',
          color: '#f59e0b',
          shape: 'line',
        });
        if (chartData.actualLineData.length) {
          items.push({
            key: 'actual-line',
            label: 'Actual value',
            description: 'Observed target values returned by the replay.',
            color: '#22c55e',
            shape: 'line',
          });
        }
        if (chartData.residualData.length) {
          items.push({
            key: 'residual',
            label: 'Residual bars',
            description: 'Prediction error. Green is positive; red is negative.',
            color: '#ef4444',
            secondaryColor: '#22c55e',
            shape: 'bar',
          });
        }
      } else if (mode === 'action') {
        classLabels.forEach((label) => {
          const wasPredicted = predictedLabelSet.has(label);
          items.push({
            key: `action-${label}`,
            label,
            description: wasPredicted
              ? 'Action marker and action timeline color.'
              : 'Expected action class was not predicted in this replay window.',
            color: labelColor(label),
            shape: 'dot',
          });
        });
      } else {
        classLabels.forEach((label) => {
          const wasPredicted = predictedLabelSet.has(label);
          items.push({
            key: `class-${label}`,
            label,
            description: !wasPredicted
              ? 'Expected class was not predicted in this replay window.'
              : mode === 'classification'
              ? 'Class marker and class timeline color.'
              : 'Structured output marker and timeline color.',
            color: labelColor(label),
            shape: 'dot',
          });
        });
      }
    }
    if (showEvents && chartData.eventPoints.length) {
      items.push({
        key: 'events',
        label: 'News / KG events',
        description: 'Event markers aligned to the closest replay bar.',
        color: '#a78bfa',
        shape: 'square',
      });
    }
    if (showConfidence && chartData.confidenceData.length) {
      items.push({
        key: 'confidence',
        label: 'Confidence',
        description: 'Probability/confidence line in a 0-1 pane.',
        color: '#22c55e',
        shape: 'line',
      });
    }
    if (showVolume && chartData.volumeData.length) {
      items.push({
        key: 'volume',
        label: 'Volume',
        description: 'Volume histogram pane.',
        color: '#94a3b8',
        shape: 'bar',
      });
    }
    return items;
  }, [
    chartData.actualLineData.length,
    chartData.confidenceData.length,
    chartData.eventPoints.length,
    chartData.priceLike,
    chartData.residualData.length,
    chartData.volumeData.length,
    classLabels,
    predictedLabelSet,
    mode,
    showConfidence,
    showEvents,
    showPredictions,
    showVolume,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !chartData.points.length) return undefined;

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const chart = createChart(container, {
      width: container.clientWidth || 800,
      height: container.clientHeight || 420,
      layout: {
        background: { color: '#080d1d' },
        textColor: '#d1d5db',
      },
      grid: {
        vertLines: { color: 'rgba(148,163,184,0.14)' },
        horzLines: { color: 'rgba(148,163,184,0.14)' },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderColor: 'rgba(148,163,184,0.25)',
      },
      timeScale: {
        borderColor: 'rgba(148,163,184,0.25)',
        timeVisible: true,
        secondsVisible: false,
      },
    } as any);
    chartRef.current = chart;

    const priceSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderVisible: false,
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
      priceLineVisible: false,
    } as any);
    priceSeries.setData(chartData.candles as any);

    let nextPane = 1;
    if (showPredictions && mode === 'regression' && chartData.predictionLineData.length) {
      const pane = chartData.priceLike ? 0 : nextPane;
      const predictionSeries = chart.addSeries(LineSeries, {
        color: '#f59e0b',
        lineWidth: 2,
        priceLineVisible: false,
        title: chartData.priceLike ? 'prediction' : 'prediction value',
      } as any, pane);
      predictionSeries.setData(chartData.predictionLineData as any);
      if (!chartData.priceLike) {
        try { predictionSeries.getPane?.().setHeight?.(120); } catch { /* chart pane API is optional */ }
        nextPane += 1;
      }
      if (chartData.actualLineData.length) {
        const actualSeries = chart.addSeries(LineSeries, {
          color: '#22c55e',
          lineWidth: 1.5,
          priceLineVisible: false,
          title: 'actual',
        } as any, pane);
        actualSeries.setData(chartData.actualLineData as any);
      }
      if (chartData.residualData.length) {
        const residualSeries = chart.addSeries(HistogramSeries, {
          priceScaleId: 'residuals',
          priceLineVisible: false,
          title: 'residual',
        } as any, nextPane);
        residualSeries.setData(chartData.residualData as any);
        try { residualSeries.getPane?.().setHeight?.(90); } catch { /* chart pane API is optional */ }
        nextPane += 1;
      }
    }

    if (showPredictions && mode !== 'regression' && chartData.classTimeline.length) {
      const classSeries = chart.addSeries(HistogramSeries, {
        priceScaleId: 'classes',
        priceLineVisible: false,
        title: mode === 'action' ? 'action timeline' : 'class timeline',
      } as any, nextPane);
      classSeries.setData(chartData.classTimeline as any);
      try { classSeries.getPane?.().setHeight?.(68); } catch { /* chart pane API is optional */ }
      try { chart.priceScale('classes')?.applyOptions?.({ visible: false, borderVisible: false }); } catch { /* optional */ }
      nextPane += 1;
    }

    if (showConfidence && chartData.confidenceData.length) {
      const confidenceSeries = chart.addSeries(LineSeries, {
        color: '#22c55e',
        lineWidth: 1.5,
        priceScaleId: 'confidence',
        priceLineVisible: false,
        title: 'confidence',
      } as any, nextPane);
      confidenceSeries.setData(chartData.confidenceData as any);
      try { confidenceSeries.getPane?.().setHeight?.(82); } catch { /* chart pane API is optional */ }
      try {
        chart.priceScale('confidence')?.applyOptions?.({
          visible: true,
          borderVisible: false,
          scaleMargins: { top: 0.1, bottom: 0.1 },
        });
      } catch { /* optional */ }
      nextPane += 1;
    }

    if (showVolume && chartData.volumeData.length) {
      const volumeSeries = chart.addSeries(HistogramSeries, {
        priceScaleId: 'volume',
        priceFormat: { type: 'volume' },
        priceLineVisible: false,
        title: 'volume',
      } as any, nextPane);
      volumeSeries.setData(chartData.volumeData as any);
      try { volumeSeries.getPane?.().setHeight?.(78); } catch { /* chart pane API is optional */ }
      try { chart.priceScale('volume')?.applyOptions?.({ visible: false, borderVisible: false }); } catch { /* optional */ }
    }

    const markers: any[] = [];
    if (showPredictions && mode !== 'regression') {
      chartData.predictions.forEach((prediction) => {
        const intent = labelIntent(prediction.label);
        markers.push({
          time: prediction.time,
          position: intent === 'bullish' ? 'belowBar' : intent === 'bearish' ? 'aboveBar' : 'inBar',
          color: labelColor(prediction.label),
          shape: intent === 'bullish' ? 'arrowUp' : intent === 'bearish' ? 'arrowDown' : 'circle',
          text: prediction.label,
        });
      });
    }
    if (showEvents) {
      chartData.eventPoints.forEach((event) => {
        markers.push({
          time: event.time,
          position: 'aboveBar',
          color: '#a78bfa',
          shape: 'square',
          text: event.event_type || 'event',
        });
      });
    }
    if (markers.length) {
      const markerApi = createSeriesMarkers(priceSeries as any);
      markerApi.setMarkers(markers.sort((a, b) => Number(a.time) - Number(b.time)));
    }

    chart.subscribeCrosshairMove((param: any) => {
      if (!param?.time || !chartData.hoverByTime.size) {
        setHover(null);
        return;
      }
      const time = typeof param.time === 'number'
        ? param.time
        : parseChartTime(`${param.time.year}-${param.time.month}-${param.time.day}`, 0);
      setHover(chartData.hoverByTime.get(time) || null);
    });

    chart.timeScale().fitContent();
    const resizeObserver = new ResizeObserver(() => {
      chart.resize(container.clientWidth || 800, container.clientHeight || 420);
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
    };
  }, [
    chartData,
    isFullscreen,
    mode,
    showConfidence,
    showEvents,
    showPredictions,
    showVolume,
  ]);

  const primaryLabel = visualizationHints.primary || (
    mode === 'regression' ? 'prediction line' : mode === 'action' ? 'action markers' : 'class markers'
  );

  const chartContent = (
    <Paper
      variant="outlined"
      sx={{
        position: isFullscreen ? 'fixed' : 'relative',
        inset: isFullscreen ? 8 : 'auto',
        zIndex: isFullscreen ? 2147483001 : 'auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: compact ? 320 : 500,
        height: isFullscreen ? 'calc(100dvh - 16px)' : compact ? 320 : 500,
        bgcolor: '#080d1d',
        overflow: 'hidden',
        borderRadius: isFullscreen ? 1.5 : 1,
        boxShadow: isFullscreen ? '0 24px 80px rgba(0,0,0,0.72)' : undefined,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ p: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'rgba(17,18,23,0.92)' }}
      >
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
          {metricChip('mode', modeLabel(mode))}
          {outputType || preview.summary.output_type ? metricChip('output', outputType || preview.summary.output_type) : null}
          {metricChip('primary', primaryLabel)}
          {metricChip('predictions', preview.predictionRows.length)}
          {classLabels.length ? metricChip('classes', classLabels.length) : null}
          {missingPredictedClasses.length ? metricChip('missing predicted', missingPredictedClasses.join(', ')) : null}
          {preview.confidencePanel.available ? metricChip('confidence', compactPercent(preview.confidencePanel.summary.avg_confidence)) : null}
          {chartData.priceLike ? metricChip('scale', 'price') : mode === 'regression' ? metricChip('scale', 'value pane') : null}
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
          <FormControlLabel
            control={<Switch size="small" checked={showPredictions} onChange={(event) => setShowPredictions(event.target.checked)} />}
            label="Predictions"
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={<Switch size="small" checked={showEvents} onChange={(event) => setShowEvents(event.target.checked)} />}
            label="Events"
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={<Switch size="small" checked={showConfidence} onChange={(event) => setShowConfidence(event.target.checked)} />}
            label="Confidence"
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={<Switch size="small" checked={showVolume} onChange={(event) => setShowVolume(event.target.checked)} />}
            label="Volume"
            sx={{ m: 0 }}
          />
          <Tooltip title="Fit chart">
            <IconButton size="small" onClick={() => chartRef.current?.timeScale?.().fitContent?.()}>
              <ZoomOutMapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? 'Exit full screen' : 'Full screen'}>
            <IconButton size="small" onClick={() => setIsFullscreen((value) => !value)}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Box
        sx={{
          px: 1,
          py: 0.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'rgba(8,13,29,0.96)',
          overflowX: 'auto',
        }}
      >
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', mr: 0.5 }}>
            Legend
          </Typography>
          {legendItems.map((item) => (
            <Tooltip key={item.key} title={item.description}>
              <Chip
                size="small"
                variant="outlined"
                icon={<LegendSwatch item={item} />}
                label={item.label}
                sx={{
                  '& .MuiChip-icon': {
                    ml: 0.75,
                    mr: -0.25,
                    color: 'inherit',
                    display: 'inline-flex',
                    alignItems: 'center',
                  },
                }}
              />
            </Tooltip>
          ))}
        </Stack>
      </Box>

      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }} />
        {hover ? (
          <Paper
            variant="outlined"
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              p: 1,
              maxWidth: 420,
              bgcolor: 'rgba(17,18,23,0.92)',
              backdropFilter: 'blur(6px)',
              pointerEvents: 'none',
            }}
          >
            <Stack spacing={0.4}>
              <Typography variant="caption" color="text.secondary">
                {formatHoverTime(hover.price?.timestamp)}
              </Typography>
              <Typography variant="body2">
                Close {compactNumber(hover.price?.close, 5)}
              </Typography>
              {hover.prediction ? (
                <Typography variant="body2">
                  Prediction {hover.prediction.label}
                  {hover.prediction.confidence !== null ? ` (${compactPercent(hover.prediction.confidence)})` : ''}
                  {hover.prediction.numeric !== null && mode === 'regression' ? ` | value ${compactNumber(hover.prediction.numeric, 5)}` : ''}
                  {hover.prediction.actual !== null ? ` | actual ${compactNumber(hover.prediction.actual, 5)}` : ''}
                </Typography>
              ) : null}
              {(hover.events || []).slice(0, 2).map((event: any) => (
                <Typography key={`${event.source}-${event.id}`} variant="caption" color="text.secondary">
                  {event.event_type}: {event.title}
                </Typography>
              ))}
            </Stack>
          </Paper>
        ) : null}
      </Box>
    </Paper>
  );

  if (isFullscreen && typeof document !== 'undefined') {
    return createPortal(
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 2147483000,
          bgcolor: 'rgba(0,0,0,0.55)',
        }}
      >
        {chartContent}
      </Box>,
      document.body,
    );
  }

  return chartContent;
};

const ModelLabPredictionTestingPanel: React.FC<ModelLabPredictionTestingPanelProps> = ({
  modelAssetId,
  compact = false,
}) => {
  const queryClient = useQueryClient();
  const [assetId, setAssetId] = useState(modelAssetId ? String(modelAssetId) : '');
  const [versionId, setVersionId] = useState('');
  const [deploymentId, setDeploymentId] = useState('');
  const [symbolsText, setSymbolsText] = useState('EURUSD');
  const [timeframe, setTimeframe] = useState('H1');
  const [bars, setBars] = useState('500');
  const [rows, setRows] = useState('250');
  const [activeSymbol, setActiveSymbol] = useState('EURUSD');
  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  useEffect(() => {
    if (modelAssetId) {
      setAssetId(String(modelAssetId));
    }
  }, [modelAssetId]);

  const assetsQuery = useQuery({
    queryKey: ['model-lab-prediction-test-assets'],
    queryFn: () => modelLabAssetApi.list({ page_size: 100, is_active: true }),
    enabled: !modelAssetId,
  });

  const selectedAssetId = assetId ? Number(assetId) : null;
  const handleModelLabEvent = useCallback((event: { type: string; payload?: Record<string, unknown> }) => {
    if (event.type === 'prediction_test.updated') {
      const testRun = event.payload?.test_run as ModelLabPredictionTestRun | undefined;
      if (testRun?.id) {
        queryClient.setQueryData(['model-lab-prediction-test-run', testRun.id], (current: unknown) => ({
          ...(current && typeof current === 'object' ? current as Record<string, unknown> : {}),
          ...testRun,
        }));
        if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(String(testRun.status))) {
          queryClient.invalidateQueries({ queryKey: ['model-lab-prediction-test-run', testRun.id] });
        }
      }
    }
    if (event.type === 'deployment.updated') {
      queryClient.invalidateQueries({ queryKey: ['model-lab-prediction-test-deployments', selectedAssetId] });
    }
  }, [queryClient, selectedAssetId]);
  const refreshModelLabSocketState = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['model-lab-prediction-test-versions', selectedAssetId] });
    queryClient.invalidateQueries({ queryKey: ['model-lab-prediction-test-deployments', selectedAssetId] });
    queryClient.invalidateQueries({ queryKey: ['model-lab-prediction-test-run'] });
  }, [queryClient, selectedAssetId]);

  useModelLabEventsSocket({
    scope: selectedAssetId ? `asset_${selectedAssetId}` : 'all',
    enabled: Boolean(selectedAssetId),
    onEvent: handleModelLabEvent,
    onReconnect: refreshModelLabSocketState,
  });

  const versionsQuery = useQuery({
    queryKey: ['model-lab-prediction-test-versions', selectedAssetId],
    queryFn: () => modelLabVersionApi.list({ model_asset: selectedAssetId!, page_size: 100 }),
    enabled: Boolean(selectedAssetId),
  });

  const deploymentsQuery = useQuery({
    queryKey: ['model-lab-prediction-test-deployments', selectedAssetId],
    queryFn: () => modelLabDeploymentApi.list({ model_asset: selectedAssetId!, page_size: 100 }),
    enabled: Boolean(selectedAssetId),
  });

  const testRunQuery = useQuery({
    queryKey: ['model-lab-prediction-test-run', activeRunId],
    queryFn: () => modelLabPredictionTestApi.get(activeRunId!),
    enabled: Boolean(activeRunId),
    refetchInterval: false,
  });

  const assets = (assetsQuery.data?.results || []) as ModelLabAsset[];
  const versions = useMemo(
    () => ([...(versionsQuery.data?.results || [])] as ModelLabVersion[])
      .sort((a, b) => b.version_number - a.version_number),
    [versionsQuery.data?.results],
  );
  const deployments = useMemo(
    () => ([...(deploymentsQuery.data?.results || [])] as ModelLabDeployment[])
      .sort((a, b) => Number(b.is_active) - Number(a.is_active) || b.id - a.id),
    [deploymentsQuery.data?.results],
  );

  const selectedVersion = versions.find((version) => String(version.id) === versionId) || null;
  const deploymentsForVersion = useMemo(
    () => (selectedVersion
      ? deployments.filter((deployment) => deployment.model_version === selectedVersion.id)
      : deployments),
    [deployments, selectedVersion],
  );
  const selectedDeployment = deploymentsForVersion.find((deployment) => String(deployment.id) === deploymentId) || null;
  const activeDeployment = deploymentsForVersion.find((deployment) => deployment.is_active || deployment.status === 'ACTIVE') || deploymentsForVersion[0];
  const selectedSymbols = useMemo(() => parseSymbols(symbolsText), [symbolsText]);
  const activePreview = preview?.symbols.find((item) => item.symbol === activeSymbol) || preview?.symbols[0] || null;
  const activeSummary = activePreview?.summary || {};
  const activeActualMetrics = activeSummary.actual_metrics || {};
  const activeMissingPredictedClasses = Array.isArray(activeSummary.missing_predicted_classes)
    ? activeSummary.missing_predicted_classes.map((item: unknown) => String(item))
    : Array.isArray(activeActualMetrics.missing_predicted_classes)
      ? activeActualMetrics.missing_predicted_classes.map((item: unknown) => String(item))
      : [];
  const activeClassLabels = Array.isArray(activeSummary.class_labels)
    ? activeSummary.class_labels.map((item: unknown) => String(item))
    : Object.keys(activeSummary.distribution_all || activeActualMetrics.predicted_distribution || {});
  const attributionTopFeatures = activePreview?.attributionTimeline.top_features?.slice(0, 4) || [];
  const attributionChartRows = useMemo(() => {
    const rows = activePreview?.attributionTimeline.rows || [];
    const featureNames = attributionTopFeatures.slice(0, 3).map((item) => item.feature);
    return rows.map((row) => {
      const output: Record<string, number | string> = {
        index: row.index,
        label: formatTimestamp(row.timestamp ?? row.time, row.index - 1),
      };
      featureNames.forEach((feature) => {
        const found = row.top_features?.find((item) => item.feature === feature);
        output[feature] = found ? Number(found.contribution || 0) : 0;
      });
      return output;
    });
  }, [activePreview?.attributionTimeline.rows, attributionTopFeatures]);

  useEffect(() => {
    if (!modelAssetId && !assetId && assets.length > 0) {
      setAssetId(String(assets[0].id));
    }
  }, [assetId, assets, modelAssetId]);

  useEffect(() => {
    if (versions.length > 0 && (!versionId || !versions.some((version) => String(version.id) === versionId))) {
      setVersionId(String(versions[0].id));
    }
  }, [versionId, versions]);

  useEffect(() => {
    if (activeDeployment && (!deploymentId || !deploymentsForVersion.some((deployment) => String(deployment.id) === deploymentId))) {
      setDeploymentId(String(activeDeployment.id));
    } else if (!activeDeployment && deploymentId) {
      setDeploymentId('');
    }
  }, [activeDeployment, deploymentId, deploymentsForVersion]);

  useEffect(() => {
    const firstSymbol = selectedSymbols[0] || 'EURUSD';
    if (!selectedSymbols.includes(activeSymbol)) {
      setActiveSymbol(firstSymbol);
    }
  }, [activeSymbol, selectedSymbols]);

  useEffect(() => {
    if (!testRunQuery.data) return;
    const nextPreview = predictionTestToPreview(testRunQuery.data);
    setPreview(nextPreview);
    if (nextPreview.symbols[0] && !nextPreview.symbols.some((item) => item.symbol === activeSymbol)) {
      setActiveSymbol(nextPreview.symbols[0].symbol);
    }
  }, [activeSymbol, testRunQuery.data]);

  const runPreviewMutation = useMutation({
    mutationFn: async (): Promise<ModelLabPredictionTestRun> => {
      if (!deploymentId) {
        throw new Error('Select a deployment before running a prediction test.');
      }
      const symbols = selectedSymbols.length ? selectedSymbols : ['EURUSD'];
      return modelLabPredictionTestApi.create({
        deployment: Number(deploymentId),
        config: {
          symbols,
          timeframe,
          bars: Math.max(60, Math.min(20000, Number(bars) || 500)),
          rows: Math.max(0, Math.min(5000, Number(rows) || 0)),
          include_events: true,
          include_attribution: true,
          confidence_threshold: 0.55,
          max_events: 80,
          max_attribution_features: 5,
        },
      });
    },
    onSuccess: (testRun) => {
      setActiveRunId(testRun.id);
      const nextPreview = predictionTestToPreview(testRun);
      setPreview(nextPreview);
      if (nextPreview.symbols[0]) {
        setActiveSymbol(nextPreview.symbols[0].symbol);
      }
    },
  });

  const isLoadingSelections = assetsQuery.isLoading || versionsQuery.isLoading || deploymentsQuery.isLoading;
  const isRunActive = preview?.status === 'QUEUED' || preview?.status === 'RUNNING';
  const hasDeployment = Boolean(selectedDeployment);
  const hasChart = Boolean(activePreview?.chartRows.length);
  const progressPercent = Number(testRunQuery.data?.progress?.percent ?? preview?.metrics?.progress ?? 0);

  return (
    <Stack spacing={compact ? 1.5 : 2}>
      <Paper variant="outlined" sx={{ p: compact ? 1.25 : 2, bgcolor: 'background.paper' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <QueryStatsIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2">Historical prediction testing</Typography>
              {preview ? <Chip size="small" color={preview.status === 'COMPLETED' ? 'success' : preview.status === 'FAILED' ? 'error' : 'warning'} label={preview.status} /> : null}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Replay a deployment over historical bars, apply its feature recipe, and render model outputs with confidence, news/KG overlays, and feature-attribution timelines. The output contract is generic, so classifications, regressions, time-series outputs, RL actions, and custom labels use the same result format.
            </Typography>
          </Box>
          {compact ? (
            <Button
              component={RouterLink}
              to="/ai/ml/prediction-tests"
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon fontSize="small" />}
            >
              Open full tester
            </Button>
          ) : null}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: modelAssetId ? 'repeat(5, minmax(0, 1fr))' : 'repeat(6, minmax(0, 1fr))',
            },
            gap: 1,
            mt: 1.5,
            alignItems: 'start',
          }}
        >
          {!modelAssetId ? (
            <TextField
              select
              label="Model"
              size="small"
              value={assetId}
              onChange={(event) => {
                setAssetId(event.target.value);
                setVersionId('');
                setDeploymentId('');
                setPreview(null);
                setActiveRunId(null);
              }}
            >
              {assets.length === 0 ? (
                <MenuItem value="" disabled>No models available</MenuItem>
              ) : assets.map((asset) => (
                <MenuItem key={asset.id} value={String(asset.id)}>
                  {asset.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            select
            label="Version"
            size="small"
            value={versionId}
            onChange={(event) => setVersionId(event.target.value)}
            disabled={!versions.length}
          >
            {versions.length === 0 ? (
              <MenuItem value="" disabled>No versions</MenuItem>
            ) : versions.map((version) => (
              <MenuItem key={version.id} value={String(version.id)}>
                v{version.version_number} - {version.stage}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Deployment"
            size="small"
            value={deploymentId}
            onChange={(event) => setDeploymentId(event.target.value)}
            disabled={!deploymentsForVersion.length}
          >
            {deploymentsForVersion.length === 0 ? (
              <MenuItem value="" disabled>No deployments</MenuItem>
            ) : deploymentsForVersion.map((deployment) => (
              <MenuItem key={deployment.id} value={String(deployment.id)}>
                {deployment.name} - {deployment.status}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Symbols"
            size="small"
            value={symbolsText}
            onChange={(event) => setSymbolsText(event.target.value)}
            helperText="Comma separated"
          />
          <TextField
            select
            label="Timeframe"
            size="small"
            value={timeframe}
            onChange={(event) => setTimeframe(event.target.value)}
          >
            {TIMEFRAMES.map((item) => (
              <MenuItem key={item} value={item}>{item}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Bars"
            size="small"
            type="number"
            value={bars}
            onChange={(event) => setBars(event.target.value)}
            inputProps={{ min: 60, max: 20000 }}
          />
          <TextField
            label="Prediction rows"
            size="small"
            type="number"
            value={rows}
            onChange={(event) => setRows(event.target.value)}
            helperText="0 = all ready rows"
            inputProps={{ min: 0, max: 5000 }}
          />
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1.25 }}>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ flex: 1 }}>
            {selectedVersion ? metricChip('version', `v${selectedVersion.version_number}`) : metricChip('version', 'missing')}
            {selectedDeployment ? metricChip('endpoint', selectedDeployment.endpoint_slug || selectedDeployment.name) : metricChip('deployment', 'missing')}
            {metricChip('symbols', selectedSymbols.length || 0)}
            {preview ? metricChip('test', `#${preview.testRunId}`) : null}
          </Stack>
          <Button
            variant="contained"
            startIcon={runPreviewMutation.isPending || isRunActive ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon fontSize="small" />}
            onClick={() => runPreviewMutation.mutate()}
            disabled={runPreviewMutation.isPending || isLoadingSelections || !hasDeployment || isRunActive}
          >
            Run Replay
          </Button>
        </Stack>

        {isRunActive ? (
          <Box sx={{ mt: 1.25 }}>
            <LinearProgress variant={Number.isFinite(progressPercent) ? 'determinate' : 'indeterminate'} value={Math.max(0, Math.min(100, progressPercent || 0))} />
            <Typography variant="caption" color="text.secondary">
              {testRunQuery.data?.progress?.message || 'Prediction replay is running...'}
            </Typography>
          </Box>
        ) : null}

        {runPreviewMutation.error ? (
          <Alert severity="error" variant="outlined" sx={{ mt: 1.25 }}>
            {getApiErrorMessage(runPreviewMutation.error, 'Prediction test failed.')}
          </Alert>
        ) : null}
        {testRunQuery.data?.error_message ? (
          <Alert severity="error" variant="outlined" sx={{ mt: 1.25 }}>
            {testRunQuery.data.error_message}
          </Alert>
        ) : null}
        {!hasDeployment && !deploymentsQuery.isLoading && testRunQuery.data?.result?.output_type !== 'forecast' ? (
          <Alert severity="warning" variant="outlined" sx={{ mt: 1.25 }}>
            No deployment is available for this model. Create a deployment with a feature recipe before running chart-based tests.
          </Alert>
        ) : null}
      </Paper>

      {testRunQuery.data?.result?.output_type === 'forecast' ? (
        <Paper variant="outlined" sx={{ p: 2 }}><ForecastResults rows={testRunQuery.data.result.forecasts || []} /></Paper>
      ) : preview ? (
        <Paper variant="outlined" sx={{ p: compact ? 1.25 : 2, bgcolor: 'background.paper' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ mb: 1.5 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2">Chart overlay</Typography>
              <Typography variant="body2" color="text.secondary">
                Interactive replay chart with native pan/zoom/crosshair. Classification and RL outputs render as decision markers and timelines; regression and time-series outputs render as forecast lines or a value pane when they are not price-scale predictions.
              </Typography>
            </Box>
            <TextField
              select
              label="Chart symbol"
              size="small"
              value={activeSymbol}
              onChange={(event) => setActiveSymbol(event.target.value)}
              sx={{ minWidth: 170 }}
            >
              {preview.symbols.map((item) => (
                <MenuItem key={item.symbol} value={item.symbol}>{item.symbol}</MenuItem>
              ))}
            </TextField>
          </Stack>

          {activePreview?.error ? (
            <Alert severity="error" variant="outlined" sx={{ mb: 1.25 }}>
              {activePreview.error}
            </Alert>
          ) : null}

          {hasChart && activePreview ? (
            <PredictionReplayChart
              preview={activePreview}
              problemType={preview.problemType}
              outputType={activePreview.summary.output_type || preview.outputType}
              visualizationHints={preview.visualizationHints}
              compact={compact}
            />
          ) : (
            <Box
              sx={{
                height: compact ? 280 : 430,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: '#080d1d',
                p: 1,
              }}
            >
              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }} spacing={1}>
                {isRunActive ? (
                  <>
                    <CircularProgress size={28} />
                    <Typography variant="subtitle2">Replay is still running</Typography>
                    <Box sx={{ width: 'min(420px, 80%)' }}>
                      <LinearProgress
                        variant={Number.isFinite(progressPercent) ? 'determinate' : 'indeterminate'}
                        value={Math.max(0, Math.min(100, progressPercent || 0))}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      {testRunQuery.data?.progress?.message || 'Waiting for the inference worker to return chart rows.'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle2">No chart data loaded</Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      The replay did not return chart rows for this symbol. Check the test error and the deployment feature recipe.
                    </Typography>
                  </>
                )}
              </Stack>
            </Box>
          )}

          {activeMissingPredictedClasses.length ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              This replay is configured as {activeClassLabels.length || 'multi'}-class output, but no rows predicted class{' '}
              {activeMissingPredictedClasses.join(', ')}. The chart is showing the model's actual predicted classes, not the full
              label space.
            </Alert>
          ) : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
              gap: 1.5,
              mt: 1.5,
            }}
          >
            <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default', minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Confidence</Typography>
                {activePreview?.confidencePanel.available
                  ? <Chip size="small" color="success" variant="outlined" label={compactPercent(activePreview.confidencePanel.summary.avg_confidence)} />
                  : <Chip size="small" variant="outlined" label="unavailable" />}
              </Stack>
              {activePreview?.confidencePanel.available ? (
                <>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                    {metricChip('low', activePreview.confidencePanel.summary.low_confidence_count ?? 0)}
                    {metricChip('threshold', compactPercent(activePreview.confidencePanel.threshold))}
                    {metricChip('min', compactPercent(activePreview.confidencePanel.summary.min_confidence))}
                  </Stack>
                  <Box sx={{ height: 140 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={activePreview.confidencePanel.rows} margin={{ top: 8, right: 12, bottom: 6, left: -18 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="index" tick={{ fill: '#9ca3af', fontSize: 10 }} tickLine={false} />
                        <YAxis domain={[0, 1]} tick={{ fill: '#9ca3af', fontSize: 10 }} tickLine={false} />
                        <RechartsTooltip
                          contentStyle={{ background: '#111217', border: '1px solid rgba(255, 165, 0, 0.45)', borderRadius: 6 }}
                          formatter={(value: any, name: string) => [compactPercent(value), name]}
                          labelFormatter={(label) => {
                            const row = activePreview.confidencePanel.rows.find((item) => item.index === Number(label));
                            return row?.label || label;
                          }}
                        />
                        <Line type="monotone" dataKey="confidence" stroke="#22c55e" strokeWidth={2} dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey="margin" stroke="#f59e0b" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  This model did not return probabilities, so calibrated confidence is not available for this replay.
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default', minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">News and KG overlays</Typography>
                <Chip size="small" variant="outlined" label={`${activePreview?.eventOverlays.summary.event_count ?? 0} events`} />
              </Stack>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                {metricChip('edges', activePreview?.eventOverlays.summary.edge_count ?? 0)}
                {metricChip('avg conf', compactPercent(activePreview?.eventOverlays.summary.avg_confidence))}
                {metricChip('net impact', compactNumber(activePreview?.eventOverlays.summary.net_impact, 2))}
              </Stack>
              <Stack spacing={0.75} sx={{ maxHeight: 168, overflow: 'auto', pr: 0.5 }}>
                {(activePreview?.eventOverlays.events || []).slice(0, 8).map((event) => (
                  <Box key={`${event.source}-${event.id}`} sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.75 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Chip size="small" variant="outlined" label={event.event_type} />
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(event.timestamp, 0)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" noWrap title={event.title}>{event.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      impact {compactNumber(event.impact, 2)} | confidence {compactPercent(event.confidence)}
                    </Typography>
                  </Box>
                ))}
                {!(activePreview?.eventOverlays.events || []).length ? (
                  <Typography variant="body2" color="text.secondary">
                    No linked news or knowledge-graph events were found in this replay window.
                  </Typography>
                ) : null}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default', minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Attribution timeline</Typography>
                {activePreview?.attributionTimeline.available
                  ? <Chip size="small" color="primary" variant="outlined" label={activePreview.attributionTimeline.method || 'available'} />
                  : <Chip size="small" variant="outlined" label="unavailable" />}
              </Stack>
              {activePreview?.attributionTimeline.available ? (
                <>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                    {attributionTopFeatures.map((feature) => (
                      <Chip
                        key={feature.feature}
                        size="small"
                        variant="outlined"
                        label={`${feature.feature}: ${compactNumber(feature.total_abs_contribution, 3)}`}
                      />
                    ))}
                  </Stack>
                  <Box sx={{ height: 140 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={attributionChartRows} margin={{ top: 8, right: 12, bottom: 6, left: -18 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="index" tick={{ fill: '#9ca3af', fontSize: 10 }} tickLine={false} />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} tickLine={false} />
                        <RechartsTooltip
                          contentStyle={{ background: '#111217', border: '1px solid rgba(255, 165, 0, 0.45)', borderRadius: 6 }}
                          formatter={(value: any, name: string) => [compactNumber(value, 4), name]}
                        />
                        {attributionTopFeatures.slice(0, 3).map((feature, index) => (
                          <Line
                            key={feature.feature}
                            type="monotone"
                            dataKey={feature.feature}
                            stroke={['#f59e0b', '#38bdf8', '#a78bfa'][index] || '#22c55e'}
                            strokeWidth={1.75}
                            dot={false}
                            isAnimationActive={false}
                          />
                        ))}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No numeric replay features were available for attribution diagnostics.
                </Typography>
              )}
            </Paper>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>Latest predictions</Typography>
              <Stack spacing={0.75}>
                {preview.symbols.map((item) => {
                  const predictions = extractPredictions(item.result);
                  const latest = predictions[predictions.length - 1];
                  return (
                    <Stack
                      key={item.symbol}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}
                    >
                      <Typography variant="body2" fontWeight={700} sx={{ width: 82 }}>{item.symbol}</Typography>
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                        {item.error ? item.error : predictionLabel(latest)}
                      </Typography>
                      <Chip size="small" variant="outlined" label={`${predictions.length} rows`} />
                    </Stack>
                  );
                })}
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>Replay contract</Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {metricChip('deployment', preview.deploymentId)}
                {metricChip('version', preview.versionId ? `#${preview.versionId}` : 'auto')}
                {metricChip('timeframe', timeframe)}
                {metricChip('bars', bars)}
                {metricChip('output', preview.metrics?.output_type || activePreview?.summary?.output_type || '-')}
                {metricChip('problem', preview.metrics?.problem_type || '-')}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Replays use the deployment feature recipe and shared artifact inference path. Unsupported custom models fail explicitly instead of being forced into a classification/regression shape.
              </Typography>
            </Paper>
          </Box>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: compact ? 1.25 : 2, bgcolor: 'background.paper' }}>
          <Stack spacing={0.75}>
            <Typography variant="subtitle2">No test run yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a deployment and run a replay to see model outputs aligned with market bars.
            </Typography>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default ModelLabPredictionTestingPanel;
